import {
  type EoReader,
  PacketAction,
  PacketFamily,
  WarpAgreeServerPacket,
  WarpEffect,
  WarpRequestServerPacket,
  WarpType,
} from 'eolib';
import type { Client } from '@/client';
import { ADMIN_WARP_LEAVE_EFFECT_ID } from '@/consts';
import { getEmf } from '@/db';
import { EOResourceID } from '@/edf';
import { EffectAnimation, EffectTargetCharacter } from '@/render';
import { SfxId } from '@/sfx';
import { ChatChannels, ChatIcon } from '@/ui/enums';

function handleWarpRequest(client: Client, reader: EoReader) {
  const packet = WarpRequestServerPacket.deserialize(reader);
  client.sessionId = packet.sessionId;
  client.warpMapId = packet.mapId;

  switch (packet.warpType) {
    case WarpType.Local: {
      client.warpQueued = true;
      break;
    }
    case WarpType.MapSwitch: {
      const data =
        packet.warpTypeData as WarpRequestServerPacket.WarpTypeDataMapSwitch;
      getEmf(packet.mapId).then((map) => {
        if (
          !map ||
          map.rid[0] !== data.mapRid[0] ||
          map.rid[1] !== data.mapRid[1] ||
          map.byteSize !== data.mapFileSize
        ) {
          client.sessionController.requestWarpMap(packet.mapId);
          return;
        }
        client.warpQueued = true;
      });
      break;
    }
  }
}

function handleWarpAgree(client: Client, reader: EoReader) {
  const packet = WarpAgreeServerPacket.deserialize(reader);
  client.nearby = packet.nearby;

  if (
    packet.warpTypeData instanceof WarpAgreeServerPacket.WarpTypeDataMapSwitch
  ) {
    if (packet.warpTypeData.warpEffect === WarpEffect.Admin) {
      const metadata = client.getEffectMetadata(ADMIN_WARP_LEAVE_EFFECT_ID);
      client.audioController.playById(SfxId.AdminWarp);
      client.animationController.effects.push(
        new EffectAnimation(
          ADMIN_WARP_LEAVE_EFFECT_ID,
          new EffectTargetCharacter(client.playerId),
          metadata,
        ),
      );
    }
  }

  if (client.mapId !== client.warpMapId) {
    getEmf(client.warpMapId).then((map) => {
      if (!map) return;
      if (map.name) {
        client.chatController.notifyChat({
          channel: ChatChannels.System,
          message: `${client.getResourceString(EOResourceID.STATUS_LABEL_YOU_ENTERED)} ${map.name}`,
          icon: ChatIcon.NoteLeftArrow,
        });
      }
      client.mapId = client.warpMapId;
      // client.nearby was already overwritten with the NEW map's entities
      // above (line ~50), before this async map load resolves. A queued
      // projectile impact bundle still names an entity key from the OLD
      // map, which by now may collide with a live, unrelated entity on the
      // new one — so bundles must be dropped here, not released. See
      // ProjectileController.clear.
      client.setMap(map, 'drop');
      client.atlas.refresh();
      client.keyboardController.unfreeze();
    });
  } else {
    client.atlas.refresh();
    client.keyboardController.unfreeze();
  }
}

export function registerWarpHandlers(client: Client) {
  client.bus!.registerPacketHandler(
    PacketFamily.Warp,
    PacketAction.Request,
    (reader) => handleWarpRequest(client, reader),
  );
  client.bus!.registerPacketHandler(
    PacketFamily.Warp,
    PacketAction.Agree,
    (reader) => handleWarpAgree(client, reader),
  );
}
